import Flickity from "react-flickity-component"
import { animationBounceLeft } from "../ds/animation.module.css"
import { btn, btnPrimary } from "../ds/button.module.css"
import { formGroup, input } from "../ds/form.module.css"
import { IoIosArrowDown } from "react-icons/io"

import {
  ArrowDownWhite,
  QuoteIconWithColor,
  WhiteArrowRight,
} from "../ds/Shapes"
import { Camera } from "../ds/SvgIcons"
import { blogUrl } from "../services/blogLinks"
import { createLead } from "../services/signupService"
import { getFormData, scrollToElement } from "../utils/BrowserUtils"
import { TESTIMONIALS } from "./landing-page/Feebacks"
import { LayoutContext } from "./layoutContext"

import { useContext, useEffect, useRef, useState } from "react"

import { COUNTRY_CODES } from "../utils/CountryCodeUtils"
import useWindowDimensions from "../utils/useWindowDimensions"

import G2Icon from "../images/svgs/g2_red.svg"
import StarRating from "../images/svgs/star_yellow.svg"
import { unSupportedDomains } from "../utils/EmailUtils"
import { Button } from "../myne/Button/Button"
import { GoCheck } from "react-icons/go"
import { PoweringBrands } from "./PoweringBrands"
import Testimonial from "./landing-page/Testimonial"
import { BlobCircleBackground } from "./BlobCircleBackground"

function CountryCodeSelector() {
  const data = useContext(LayoutContext)

  const [showDropdown, toggleDropdown] = useState(false)
  const [countryCode, setCountryCode] = useState<string>()
  const [search, setSearch] = useState("")

  const wrapperRef = useRef(null)

  // Default country
  useEffect(() => {
    const defaultCountryCode = COUNTRY_CODES.find(
      item => item.alpha2Code === (data.userLocation?.country || "US")
    )
    setCountryCode(defaultCountryCode?.dial_code)
  }, [data.userLocation])

  // Outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        toggleDropdown(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  function onFocus() {
    toggleDropdown(true)
  }

  // Filtered list
  const filteredCountries = COUNTRY_CODES.filter(
    item =>
      item.name.toLowerCase().includes(search.toLowerCase()) ||
      item.dial_code.includes(search)
  )

  return (
    <div className="w-36 relative" ref={wrapperRef}>
      {/* Input + Icon */}
      <div className="relative">
        <input
          className={`${input} appearance-none pr-8`}
          value={countryCode}
          type="text"
          name="countryCode"
          onFocus={onFocus}
          readOnly
        />

        <IoIosArrowDown
          size={16}
          className="absolute text-white right-4 top-1/2 -translate-y-1/2 pointer-events-none"
        />
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div className="shadow-xl border bg-white p-2 w-80 h-96 absolute rounded-xl mt-2 z-10 overflow-hidden">
          <input
            type="text"
            placeholder="Search country..."
            className="w-full px-3 py-2 mb-2 border rounded-lg text-sm outline-none"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoComplete="off"
          />

          <div className="h-[calc(100%-50px)] overflow-y-auto">
            {filteredCountries.map(item => (
              <p
                key={item.alpha2Code}
                className="flex items-center justify-between text-sm py-2 px-2 font-semibold cursor-pointer hover:bg-zinc-100 rounded-xl"
                onClick={() => {
                  setCountryCode(item.dial_code)
                  toggleDropdown(false)
                  setSearch("")
                }}
              >
                <span className="inline-block w-14 text-left">
                  {item.dial_code}
                </span>
                <span className="inline-block flex-1 text-left">
                  {item.name}
                </span>
              </p>
            ))}
            {filteredCountries?.length === 0 && (
              <p className="text-center text-sm py-2 px-2 font-semibold cursor-pointer hover:bg-zinc-100 rounded-xl">
                No country found
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
function TestimonialMiniCard(props) {
  const { data } = props
  return (
    <div
      className="rounded-2xl p-4 md:p-10 relative md:min-h-[330px] min-h-[440px] mx-2 flex flex-col justify-between h-full"
      style={{ backgroundColor: data?.lightestColor }}
    >
      <div className="absolute left-10 -top-2">
        <QuoteIconWithColor color={data.color} />
      </div>
      <div className="top pt-6 md:pt-0">
        <div className="md:hidden mb-5 flex justify-center">{data.logo}</div>
        <p className="text-black text-sm mb-5 h-24">{data?.message}</p>
      </div>
      <div className="bottom flex items-center">
        <div className="rounded-full mr-5">{data.profile}</div>
        <div>
          <h4 className="text-sm font-extrabold text-black text-left">
            {data?.name}
          </h4>
          <p className="text-xs text-left">{data?.designation || ""}</p>
          <p className="text-xs text-left">{data?.company || ""}</p>
        </div>
        <div className="hidden md:block ml-auto">{data.logo}</div>
      </div>
    </div>
  )
}

function ReviewSection() {
  const { width } = useWindowDimensions()
  const isMobileUp = width >= 768

  const flickityOptions = {
    autoPlay: 3000,
    wrapAround: true,
    pageDots: false,
    prevNextButtons: true,
  }

  return (
    <div>
      {/* Carousel Section */}
      <div className="relative overflow-scroll no-scrollbar pt-10">
        <Flickity
          className="carousel outline-none"
          options={flickityOptions}
          reloadOnUpdate
        >
          {TESTIMONIALS.map((item, index) => (
            <TestimonialMiniCard key={index} data={item} />
          ))}
        </Flickity>
      </div>

      {/* Ratings Section */}
      <div className="flex items-center my-10">
        <img
          src={G2Icon}
          alt="G2 Icon"
          width="24"
          height="24"
          loading="lazy"
          className="mt-1"
        />
        <img
          src={StarRating}
          alt="Star Rating"
          width="88"
          height="16"
          loading="lazy"
          className="ml-3"
        />
        <p className="ml-2 mt-1 text-sm">4.8 ratings</p>
      </div>
    </div>
  )
}

export default function RequestDemo() {
  const [isLoading, setLoading] = useState(false)
  const [error, setErrors] = useState(null)
  const [checked, setChecked] = useState(false)

  const isBrowser = () => typeof window !== "undefined"

  const isUAEDomain = isBrowser() && window.location.host.includes("ae")

  function handleFormSubmit(e) {
    const data = getFormData(e)
    const phonereg = /^\d{8,13}$/

    if (unSupportedDomains.includes(data.email.split("@")[1])) {
      setErrors({
        email: `Please enter a different email. <b>${
          data.email.split("@")[1]
        }</b> emails are not allowed.`,
      })
      return
    }
    if (
      data.first_name.trim().length &&
      data.last_name.trim().length &&
      data.company_name.trim().length &&
      data.phone.replaceAll(" ", "").match(phonereg) !== null
    ) {
      setErrors(null)
      setLoading(true)
      createLead({
        first_name: data.first_name.trim(),
        last_name: data.last_name.trim(),
        company_name: data.company_name.trim(),
        name: `${data.first_name.trim()} ${data.last_name.trim()}`,
        email: data.email,
        phone: `${data.countryCode}${data.phone.replaceAll(" ", "")}`,
        avg_events_per_day: data.avg_events_per_day,
        referral_source: data.referral_source,
        lead_profile: "EVENT_PLANNER",
      })
        .then(response => {
          setLoading(false)
          try {
            // @ts-ignore
            window.AdvocateTracking.trackPurchase({
              orderId: new Date().toString(),
              orderCount: 1,
              value: 120.0,
              currency: "USD",
            })
          } catch (e) {
            console.log(
              `Tracking widget not loaded: ${e}. Make sure Adblocker is not blocking execution. If you are on cvent, contact support@premagic.com`
            )
          }
          window.location.href = `${window.location.origin}${window.location.pathname}success/`
        })
        .catch(data => {
          setErrors(data.error)
          scrollToElement(".js-error", true)
          setLoading(false)
        })

      return
    }
    setErrors("Please check the details you have provided")
  }

  const { width } = useWindowDimensions()
  const isMobileUp = width >= 1080
  const isMobile = width < 768
  const containerWidth = isMobileUp ? "1080px" : "100%"

  return (
    <section className="flex container mx-auto px-6 flex-wrap flex-col md:flex-row mx-auto md:my-20 px-5 relative">
      {isMobileUp && <BlobCircleBackground />}
      <div className="md:w-1/2 md:p-10 md:p-0 mt-10 md:mt-0 md:pr-16 ">
        <h2 className="text-black font-semibold text-3xl md:text-4xl ">
          Book a Personalized Premagic Demo
        </h2>
        <p className="text-[#121212] text-md font-tin mb-5 mt-5 max-w-md">
          See how Premagic enables attendees to create authentic user-generated
          content that goes beyond traditional advertising.
        </p>
        <ul>
          {[
            "See real event use cases",
            "Explore campaign ideas",
            "Get a guided walkthrough",
          ].map((item, index) => (
            <li key={index} className="flex items-center mb-2">
              <span className="w-8 h-8 flex items-center justify-center bg-[#e4f8f0] text-[#38C172] rounded-full p-1">
                <GoCheck />
              </span>
              <p className="ml-2 !text-md ml-2">{item}</p>
            </li>
          ))}
        </ul>
        {/* {!isMobile && <ReviewSection />} */}
        <PoweringBrands isDemoPage />
        {isMobileUp && <Testimonial isDemoPage />}
      </div>
      <div className="md:w-1/2 relative z-10">
        <div
          className="px-6 md:px-10 md:px-20 py-10 rounded-3xl mt-6 md:mt-0"
          style={{ backgroundColor: "#7251F4" }}
        >
          <h2 className="text-white font-semibold text-xl mb-8">
            Tell us about your event
          </h2>

          <form onSubmit={handleFormSubmit} id="account-lead-events">
            {error && !error?.email && (
              <div
                className="js-error bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-10"
                role="alert"
              >
                <strong className="font-bold">Oops!</strong>{" "}
                <span className="block sm:inline">{error}</span>
              </div>
            )}

            <div className="flex gap-4">
              <div className={formGroup}>
                <p className="text-white font-semibold text-base mb-1">
                  First name
                </p>
                <input
                  required
                  type="text"
                  name="first_name"
                  autoFocus
                  placeholder=""
                  className={input}
                />
              </div>
              <div className={formGroup}>
                <p className="text-white font-semibold text-base mb-1">
                  Last name
                </p>
                <input
                  required
                  type="text"
                  name="last_name"
                  placeholder=""
                  className={input}
                />
              </div>
            </div>
            <div className={`${formGroup} mt-4`}>
              <p className="text-white font-semibold text-base mb-1">
                Company name
              </p>
              <input
                required
                type="text"
                name="company_name"
                placeholder=""
                className={input}
              />
            </div>
            <div className={`${formGroup} mt-4`}>
              <p className="text-white font-semibold text-base mb-1">
                Work Email
              </p>
              <input
                required
                type="email"
                name="email"
                placeholder="eg. david@company.com"
                style={error?.email ? { border: "2px solid #FF6550" } : {}}
                className={input}
              />
              {error?.email && (
                <span
                  className="block mt-2"
                  style={{ color: "#FF6550" }}
                  dangerouslySetInnerHTML={{ __html: error?.email }}
                ></span>
              )}
            </div>
            <div className="mt-4">
              <p className="text-white font-semibold text-base mb-1">
                Phone number
              </p>
              <div className="flex ">
                <CountryCodeSelector />
                <div className="px-2" />
                <input
                  required
                  type="tel"
                  name="phone"
                  id="input-number"
                  placeholder="eg. 9XX XXX XXXX"
                  minLength={8}
                  maxLength={13}
                  className={input}
                />
              </div>
            </div>

            <div className={`${formGroup} mt-4 relative`}>
              <select
                name="avg_events_per_day"
                placeholder="Average events per month"
                className={`${input} appearance-none`}
                defaultValue=""
              >
                <option value="" disabled hidden>
                  Average events per year
                </option>
                <option value="<2">&lt;2</option>
                <option value="2-5">2-5</option>
                <option value="5-10">5-10</option>
                <option value="11-25">11-25</option>
                <option value="26-100">26-100</option>
                <option value="100+">100+</option>
              </select>
              <div className="absolute top-1/2 right-5 -translate-y-1/2 opacity-60">
                <ArrowDownWhite />
              </div>
            </div>

            <div className={`${formGroup} mt-4 relative`}>
              <select
                required
                name="referral_source"
                className={`${input} appearance-none`}
                defaultValue=""
              >
                <option value="" disabled hidden>
                  How did you hear about us?
                </option>
                <option value="Google Search">Google Search</option>
                <option value="Social Media">
                  Social Media (LinkedIn, Facebook, Twitter)
                </option>
                <option value="Referral">Referral from colleague/friend</option>
                <option value="Industry Event">
                  Industry event/conference
                </option>
                <option value="Email Marketing">Email marketing</option>
                <option value="Content/Blog">Content/Blog</option>
                <option value="Online Advertisement">
                  Online advertisement
                </option>
                <option value="Partner Recommendation">
                  Partner recommendation
                </option>
                <option value="Word of Mouth">Word of mouth</option>
                <option value="Other">Other</option>
              </select>
              <div className="absolute top-1/2 right-5 -translate-y-1/2 opacity-60">
                <ArrowDownWhite />
              </div>
            </div>

            <div className="text-white mt-5 text-sm flex items-start">
              <input
                className="input mr-2 mt-1"
                type="checkbox"
                onChange={e => setChecked(e.target.checked)}
              />
              <div>
                I agree to Premagic's{" "}
                <a
                  href={blogUrl("terms")}
                  className="text-white font-bold"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href={blogUrl("privacy")}
                  className="text-white font-bold"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>
                .
              </div>
            </div>
            <input type="hidden" id="zc_gad" name="zc_gad" value="" />
            <Button
              variant="primary"
              id="submit_button_createlead"
              className={`${btn} ${btnPrimary} bg-white mt-10`}
              type="submit"
              disabled={!checked}
            >
              {isLoading ? `Loading...` : `Get a Demo`}
            </Button>
          </form>
        </div>
        {!isMobileUp && <Testimonial isDemoPage />}
      </div>
    </section>
  )
}
